import React, { useState, useEffect } from 'react'
import { useStrava } from '../../context/StravaContext'
import { stravaService } from '../../services/strava'
import { Walk } from '../../types/walk'
import { useToast } from '../../hooks/useToast'
import ToastContainer from '../ui/ToastContainer'
import { walkService } from '../../services/walks'

interface StravaConnectionProps {
  walks: Walk[]
  onWalksUpdate: React.Dispatch<React.SetStateAction<Walk[]>>
  onActivitiesRefresh?: () => void
}

const StravaConnection: React.FC<StravaConnectionProps> = ({ walks, onWalksUpdate, onActivitiesRefresh }) => {
  const { 
    isConnected, 
    athlete, 
    activities,
    isLoading, 
    error, 
    connectStrava, 
    disconnectStrava,
    loadActivities 
  } = useStrava()

  const [importingActivities, setImportingActivities] = useState<Set<string>>(new Set())
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set())
  const [selectedWalkId, setSelectedWalkId] = useState<string>('')
  const [importedActivities, setImportedActivities] = useState<Map<string, { walk_id: string; walk_name: string }>>(new Map())
  const [loadingImportStatus, setLoadingImportStatus] = useState(false)
  const { messages, showSuccess, showError, removeToast } = useToast()

  const refreshWalks = async () => {
    try {
      const updatedWalks = await walkService.getAllWalks()
      onWalksUpdate(updatedWalks)
      console.log('Walks refreshed successfully:', updatedWalks.length, 'walks')
    } catch (error) {
      console.error('Failed to refresh walks:', error)
      showError('Refresh Failed', 'Failed to refresh walk statistics. Please refresh the page.')
    }
  }

  const loadImportedActivities = async () => {
    setLoadingImportStatus(true)
    try {
      const allActivities = await walkService.getAllWalkActivities()
      const importMap = new Map<string, { walk_id: string; walk_name: string }>()
      
      for (const activity of allActivities) {
        // Store with the string version of the ID for consistent lookup
        importMap.set(activity.strava_activity_id, {
          walk_id: activity.walk_id,
          walk_name: activity.walk_name
        })
      }
      
      setImportedActivities(importMap)
    } catch (error) {
      console.error('Failed to load imported activities:', error)
    } finally {
      setLoadingImportStatus(false)
    }
  }

  // Load imported activities when component mounts or activities change
  useEffect(() => {
    if (activities.length > 0 && walks.length > 0) {
      loadImportedActivities()
    }
  }, [activities.length, walks.length])

  const handleActivityToggle = (activityId: string) => {
    setSelectedActivities(prev => {
      const newSet = new Set(prev)
      if (newSet.has(activityId)) {
        newSet.delete(activityId)
      } else {
        newSet.add(activityId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    const notImportedActivities = recentActivities.filter(
      activity => !importedActivities.has(activity.id)
    )
    
    if (selectedActivities.size === notImportedActivities.length && notImportedActivities.length > 0) {
      setSelectedActivities(new Set())
    } else {
      setSelectedActivities(new Set(notImportedActivities.map(a => a.id)))
    }
  }

  const handleBatchImport = async () => {
    if (!selectedWalkId) {
      showError('No Walk Selected', 'Please select a walk to import activities into.')
      return
    }

    if (selectedActivities.size === 0) {
      showError('No Activities Selected', 'Please select at least one activity to import.')
      return
    }

    const activitiesToImport = recentActivities.filter(activity => selectedActivities.has(activity.id))
    setImportingActivities(new Set(activitiesToImport.map(a => a.id)))

    try {
      // Get a valid access token (refreshes automatically if expired)
      const accessToken = await stravaService.getValidAccessToken()
      if (!accessToken) {
        throw new Error('No Strava access token found. Please reconnect to Strava.')
      }

      // Get admin token for authentication
      const adminToken = localStorage.getItem('adminToken')
      if (!adminToken) {
        throw new Error('Admin authentication required')
      }

      const results = []
      const errors = []

      // Import activities one by one
      for (const activity of activitiesToImport) {
        try {
          const response = await fetch('/api/strava-import', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${adminToken}`
            },
            body: JSON.stringify({
              activityId: activity.id,
              walkId: selectedWalkId,
              accessToken: accessToken
            })
          })

          if (!response.ok) {
            const error = await response.json().catch(() => ({}))
            throw new Error(error.details || error.error || `Import failed: ${response.statusText}`)
          }

          const result = await response.json()
          results.push({
            activity: activity.name,
            result
          })
        } catch (error) {
          console.error(`Failed to import activity ${activity.name}:`, error)
          errors.push({
            activity: activity.name,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
        }
      }

      // Show results
      if (results.length > 0) {
        showSuccess(
          'Import Successful!', 
          `Successfully imported ${results.length} activit${results.length === 1 ? 'y' : 'ies'}`
        )
        // Refresh walks to update statistics (small delay to ensure DB is updated)
        setTimeout(async () => {
          await refreshWalks()
          await loadImportedActivities()
          // Also refresh cached activities in WalkManagement
          if (onActivitiesRefresh) {
            onActivitiesRefresh()
          }
        }, 1000)
      }

      if (errors.length > 0) {
        showError(
          'Some Imports Failed', 
          `${errors.length} activit${errors.length === 1 ? 'y' : 'ies'} failed to import. Check console for details.`
        )
      }
      
      // Clear selections
      setSelectedActivities(new Set())
      setSelectedWalkId('')
      
    } catch (error) {
      console.error('Failed to import activities:', error)
      showError(
        'Import Failed', 
        error instanceof Error ? error.message : 'An unknown error occurred during import'
      )
    } finally {
      setImportingActivities(new Set())
    }
  }

  // Filter activities to last 2 years and sort by newest first
  const recentActivities = activities
    .filter(activity => {
      const activityDate = new Date(activity.start_date)
      const twoYearsAgo = new Date()
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2)
      return activityDate >= twoYearsAgo
    })
    .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())

  if (error) {
    return (
      <>
        <ToastContainer messages={messages} onClose={removeToast} />
        <div className="bg-red-50/80 backdrop-blur-lg border border-red-200/60 rounded-2xl p-6 shadow-lg">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-900 mb-1">Strava Connection Error</h3>
            <p className="text-red-700 text-sm leading-relaxed">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-3 inline-flex items-center px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
      </>
    )
  }

  if (!isConnected) {
    return (
      <>
        <ToastContainer messages={messages} onClose={removeToast} />
        <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-red-500 rounded-2xl mb-4">
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-.3c-.615 0-1.115-.516-1.115-1.158s.5-1.158 1.115-1.158h.3l2.089-4.116c.242-.477.895-.515 1.185-.074l5.546 8.421c.242.369.048.875-.4.875h-6.331zm-3.896-6.463L9.4 7.365c-.242-.477-.895-.515-1.185-.074L2.669 15.712c-.242.369-.048.875.4.875h6.331l2.089-4.116h.3c.615 0 1.115-.516 1.115-1.158s-.5-1.158-1.115-1.158h-.3z"/>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Connect to Strava
            </h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Import your hiking activities and create beautiful visualizations of your multi-day adventures.
            </p>
          </div>

          <div className="space-y-4">
            <button
              type="button"
              onClick={connectStrava}
              disabled={isLoading}
              className="w-full inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting to Strava...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.387 17.944l-2.089-4.116h-.3c-.615 0-1.115-.516-1.115-1.158s.5-1.158 1.115-1.158h.3l2.089-4.116c.242-.477.895-.515 1.185-.074l5.546 8.421c.242.369.048.875-.4.875h-6.331zm-3.896-6.463L9.4 7.365c-.242-.477-.895-.515-1.185-.074L2.669 15.712c-.242.369-.048.875.4.875h6.331l2.089-4.116h.3c.615 0 1.115-.516 1.115-1.158s-.5-1.158-1.115-1.158h-.3z"/>
                  </svg>
                  Connect with Strava
                </>
              )}
            </button>
            
            <div className="bg-emerald-50/80 border border-emerald-200/60 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-emerald-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800 mb-1">OAuth Integration Ready</p>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    Secure OAuth flow is fully implemented. Click "Connect with Strava" to authorize access to your hiking activities via our secure backend endpoint.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    )
  }

  return (
    <>
      <ToastContainer messages={messages} onClose={removeToast} />
      <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg overflow-hidden">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <img 
                className="h-12 w-12 rounded-xl object-cover border-2 border-white shadow-lg" 
                src={athlete?.profile || '/default-avatar.png'} 
                alt={`${athlete?.firstname} ${athlete?.lastname}`}
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white"></div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {athlete?.firstname} {athlete?.lastname}
              </h3>
              <p className="text-sm text-slate-500">
                {[athlete?.city, athlete?.state, athlete?.country].filter(Boolean).join(', ')}
              </p>
              <div className="flex items-center mt-1">
                <svg className="w-3 h-3 text-emerald-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-emerald-600 font-medium">Connected</span>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={loadActivities}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-medium rounded-xl transition-all duration-200 border border-emerald-200 hover:border-emerald-300 disabled:opacity-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {isLoading ? 'Loading...' : 'Refresh Activities'}
            </button>
            <button
              type="button"
              onClick={disconnectStrava}
              className="inline-flex items-center px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-xl transition-all duration-200 border border-red-200 hover:border-red-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Disconnect
            </button>
          </div>
        </div>

        {walks.length === 0 ? (
          <div className="bg-blue-50/80 border border-blue-200/60 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">No Walks Available</p>
                <p className="text-xs text-blue-700">Create a walk first to import activities into it.</p>
              </div>
            </div>
          </div>
        ) : recentActivities.length === 0 ? (
          <div className="bg-white/40 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg p-8 text-center">
            <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <h4 className="text-lg font-medium text-slate-600 mb-2">No hiking activities found</h4>
            <p className="text-slate-500 text-sm">No hiking activities found in the last 2 years</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-slate-900">Recent Hiking Activities</h4>
                <p className="text-slate-600">Select activities and import them all at once</p>
              </div>
              <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                {recentActivities.length} activities found
              </span>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl">
              <select
                value={selectedWalkId}
                onChange={(e) => setSelectedWalkId(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent min-w-48"
              >
                <option value="">Select a walk...</option>
                {walks.map((walk) => (
                  <option key={walk.id} value={walk.id}>
                    {walk.name}
                  </option>
                ))}
              </select>
              
              <button
                onClick={handleBatchImport}
                disabled={!selectedWalkId || selectedActivities.size === 0 || importingActivities.size > 0}
                className="inline-flex items-center px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importingActivities.size > 0 ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Importing {importingActivities.size}...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    Import Selected ({selectedActivities.size})
                  </>
                )}
              </button>
            </div>
            
            <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedActivities.size === recentActivities.length && recentActivities.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-emerald-600 bg-white border-slate-300 rounded focus:ring-emerald-500 focus:ring-2"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Activity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Elevation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {recentActivities.map((activity) => {
                      const importStatus = importedActivities.get(activity.id)
                      const isImported = !!importStatus
                      
                      return (
                        <tr 
                          key={activity.id} 
                          className={`hover:bg-slate-50/50 transition-colors ${
                            selectedActivities.has(activity.id) ? 'bg-emerald-50/50' : ''
                          } ${
                            importingActivities.has(activity.id) ? 'opacity-50' : ''
                          } ${
                            isImported ? 'bg-gray-50' : ''
                          }`}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedActivities.has(activity.id)}
                              onChange={() => handleActivityToggle(activity.id)}
                              disabled={importingActivities.has(activity.id) || isImported}
                              className="w-4 h-4 text-emerald-600 bg-white border-slate-300 rounded focus:ring-emerald-500 focus:ring-2 disabled:opacity-50"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-slate-900">{activity.name}</div>
                            {importingActivities.has(activity.id) && (
                              <div className="text-xs text-emerald-600 flex items-center mt-1">
                                <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Importing...
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {new Date(activity.start_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {(activity.distance / 1000).toFixed(1)} km
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {Math.round(activity.moving_time / 60)} min
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {activity.total_elevation_gain}m
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {[activity.location_city, activity.location_state, activity.location_country].filter(Boolean).join(', ') || '-'}
                          </td>
                          <td className="px-4 py-4">
                            {loadingImportStatus ? (
                              <div className="text-xs text-gray-500">Loading...</div>
                            ) : isImported ? (
                              <div className="space-y-1">
                                <div className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Imported
                                </div>
                                <div className="text-xs text-gray-600 truncate max-w-[150px]" title={importStatus.walk_name}>
                                  in "{importStatus.walk_name}"
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">Not imported</div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}

export default StravaConnection